import GameEditScreen from '../components/GameEditScreen'
import ModalBackdrop from '../components/ModalBackdrop'
import SharedElementOverlay from '../components/SharedElementOverlay'
import CreateLeagueScreen from '../components/CreateLeagueScreen'
import JoinLeagueScreen from '../components/JoinLeagueScreen'
import { getRecentForm } from '../teamForm'
import GameweekResultsModal from './GameweekResultsModal'
import TopScorerModal from '../components/TopScorerModal'
import { buildOtherPlayerFixtureStates } from './otherPlayerFixtureStates'
import { useGoldenResults } from './useGoldenResults'
import type { FixtureStatus } from '../api/football'
import type { AppView } from './AppView'

/**
 * Everything that floats over the tab shell: the fixture edit sheet, the
 * gameweek results modal (plus the shared-element clone that flies into it), and the two league
 * entry sheets. Each is independently mounted, so this is a flat list rather than a stack.
 */
export default function AppModals({ view }: { view: AppView }) {
  const { gameweek, predictions, leagues, topScorer } = view
  const { games } = gameweek
  const { editingGameId, bankerGameId } = predictions

  /**
   * The dev answer sheet, fetched here rather than in the picker: the picker cycles through every
   * fixture of the gameweek inside one modal, so a fetch inside it would be a request per card.
   * Empty in live and for everyone but the admin.
   */
  const goldenResults = useGoldenResults({
    mode: view.identity.dataMode,
    gameweek: gameweek.currentGameweek,
    enabled: view.identity.isAdmin,
  })

  const editingGame = editingGameId ? (games.find((g) => g.id === editingGameId) ?? null) : null
  const currentBankerGame = bankerGameId
    ? (games.find((g) => g.id === bankerGameId) ?? null)
    : null
  // An open fixture always has a prediction (opening one seeds it at 0-0), but fall back rather
  // than assert so a mid-flight gameweek change cannot blank the sheet.
  const editingPrediction = editingGame
    ? (predictions.predictions[editingGame.id] ?? {
        gameId: editingGame.id,
        homeScore: 0,
        awayScore: 0,
      })
    : null

  /**
   * What the picker needs to know to hide the win-probability bar.
   *
   * The live poll is the fast signal — it reports a fixture the moment it kicks off, which is the
   * instant the bar has to go — and a stored result is the backstop for one that has already
   * finished. Everything else is still pre-match.
   */
  const editingFixtureStatus: FixtureStatus = !editingGame
    ? 'scheduled'
    : (view.live.fixtures.find((fixture) => fixture.id === editingGame.id)?.status ??
      (gameweek.actualResults[editingGame.id] ? 'finished' : 'scheduled'))

  return (
    <>
      {editingGame && editingPrediction && (
        <ModalBackdrop onClose={predictions.closeEdit}>
          <GameEditScreen
            game={editingGame}
            homeScore={editingPrediction.homeScore}
            awayScore={editingPrediction.awayScore}
            homeTeamForm={getRecentForm(editingGame.homeTeam, editingGame.gameweek)}
            awayTeamForm={getRecentForm(editingGame.awayTeam, editingGame.gameweek)}
            onScoreChange={(h, a) => predictions.changeScore(editingGame.id, h, a)}
            isBanker={bankerGameId === editingGame.id}
            onToggleBanker={() => predictions.toggleBanker(editingGame)}
            pendingBankerSwitchFrom={
              predictions.isBankerSwitchPending ? currentBankerGame : null
            }
            onConfirmBankerSwitch={() => predictions.confirmBankerSwitch(editingGame)}
            onCancelBankerSwitch={predictions.cancelBankerSwitch}
            forecast={view.forecasts.byFixture[editingGame.id] ?? null}
            fixtureStatus={editingFixtureStatus}
            goldenScore={goldenResults.byFixture[editingGame.id] ?? null}
            isLastInCycle={
              games.findIndex((g) => g.id === editingGame.id) >= games.length - 1
            }
            onNext={predictions.isCycling ? predictions.nextGame : undefined}
          />
        </ModalBackdrop>
      )}

      <GameweekResultsModal view={view} />

      {topScorer.open && (
        <TopScorerModal
          gameweek={topScorer.open.gameweek}
          profile={topScorer.profile}
          seasonStats={topScorer.seasonStats}
          calibration={topScorer.calibration}
          games={games}
          predictions={topScorer.picks.predictions}
          bankerGameId={topScorer.picks.bankerGameId}
          activeChipId={topScorer.picks.activeChipId}
          fixtureStates={buildOtherPlayerFixtureStates({
            games,
            revealedResults: view.scores.revealedResults,
            simGameStatuses: view.simulation.gameStatuses,
            actualResults: gameweek.actualResults,
            liveResults: view.simulation.liveResults,
          })}
          onClose={topScorer.close}
          onOpenProfile={() => {
            const opened = topScorer.open
            if (!opened) return
            topScorer.close()
            view.viewedPlayer.viewPlayer(opened.userId, opened.displayName)
          }}
        />
      )}

      <SharedElementOverlay
        data={view.history.transition.overlay}
        cloneRef={view.history.transition.cloneRef}
      />

      {leagues.isCreatingLeague && (
        <ModalBackdrop onClose={leagues.closeCreateLeague}>
          <CreateLeagueScreen
            onCreate={leagues.createLeague}
            onCancel={leagues.closeCreateLeague}
          />
        </ModalBackdrop>
      )}

      {leagues.isJoiningLeague && (
        <ModalBackdrop onClose={leagues.closeJoinLeague}>
          <JoinLeagueScreen onJoin={leagues.joinLeague} onCancel={leagues.closeJoinLeague} />
        </ModalBackdrop>
      )}
    </>
  )
}
